import equal from "fast-deep-equal";
import { identity } from "lodash-es";
import React from "react";
import { useSelector } from "react-redux";

import { SearchComponentOfType } from "@utils/search/@types/searchComponent";
import { getInvalidationsForComponent } from "@utils/search/invalidation.utils";

import { getHierarchicalFacetName } from "@services/bffSearch/responseTransformers/mergeHierarchicalFacets.transformer";

import { useThunkDispatch } from "@redux/dispatcher";
import { configureSearchComponent } from "@redux/modules/value/search/search.actions";
import {
    getSelectedFacetFilters,
    getSelectedNumericFilters,
    useSearchComponentStateSelector,
} from "@redux/modules/value/search/search.selectors";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { FacetType } from "@components/Search/SearchResults/StateComponents/Facets/Facet/@types/facetType.types";
import { ClearSelectedFacetsComponent } from "@components/Search/SearchResults/StateComponents/SelectedFacets/clearSelectedFacets.component";
import { SelectedFacetComponent } from "@components/Search/SearchResults/StateComponents/SelectedFacets/selectedFacet.component";
import { getLabelMapperByFacetType } from "@components/Search/SearchResults/StateComponents/SelectedFacets/selectedFacets.rules";
import {
    Filters,
    SelectedFacet,
} from "@components/Search/SearchResults/StateComponents/SelectedFacets/selectedFacets.types";

import { useLanguage } from "@context/language.context";

type SelectedFacetsProps = {
    component: SearchComponentOfType<"FACETS">;
};

const SelectedFacets = ({ component }: SelectedFacetsProps) => {
    const facetFilters = useSelector(getSelectedFacetFilters(component), equal);
    const numericFilters = useSelector(
        getSelectedNumericFilters(component),
        equal,
    );
    const { getLocale } = useLanguage();

    const facets = useSearchComponentStateSelector(component)?.facets;

    const dispatch = useThunkDispatch();
    const { t } = useGranite();

    const mapToSelectedFacet =
        <TFilter extends keyof Filters>(filterType: TFilter) =>
        (filter: Filters[TFilter]) => {
            const facetForFilter = facets?.find(facet => {
                const facetFilterName =
                    facet.type === FacetType.HIERARCHICAL
                        ? getHierarchicalFacetName(filter.name)
                        : filter.name;

                return facetFilterName === facet.name;
            });
            if (!facetForFilter) return undefined;

            return getLabelMapperByFacetType(facetForFilter.type)(
                facetForFilter,
                { [filterType]: filter },
                dispatch,
                component,
                t,
                getLocale(),
            );
        };

    const selectedFacets = [
        ...facetFilters.flatMap(mapToSelectedFacet("facetFilter")),
        ...numericFilters.flatMap(mapToSelectedFacet("numericFilter")),
    ].filter(identity) as Array<SelectedFacet>;

    const handleOnClearAllFacets = () => {
        dispatch(
            configureSearchComponent(
                {
                    component: component,
                    facetFilters: [],
                    numericFilters: [],
                },
                getInvalidationsForComponent(component),
            ),
        );
    };

    return (
        <section className={"d-flex flex-wrap"}>
            {!!selectedFacets.length && (
                <span className="mb-2 mr-2 d-block">
                    {t("search.selectedRefinements.selected")}
                </span>
            )}
            {selectedFacets.map(selectedFacet => (
                <SelectedFacetComponent
                    key={selectedFacet.label}
                    selectedFacet={selectedFacet}
                />
            ))}
            {selectedFacets.length > 1 && (
                <ClearSelectedFacetsComponent
                    onClear={handleOnClearAllFacets}
                />
            )}
        </section>
    );
};

export { SelectedFacets };
